import { ChangeEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faColumns3, faFilter } from "@fortawesome/pro-regular-svg-icons";

import { ReactComponent as MagnifyingGlass } from "@assets/icons/list-builder/magnifying-glass.svg";
import { ReactComponent as LinkedinSvg } from "@assets/icons/linkedinRounded.svg";
import { ReactComponent as Plus } from "@assets/icons/plus.svg";

import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Button } from "@/components/Button/Button";
import { Icon } from "@/components/Icon/Icon";
import { InputField } from "@/components/InputField/InputField";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

interface LinkedinConnectionsToolbarProps {
	searchValue: string;
	onSearchChange: (value: string) => void;
	/** How many filter fields are set, shown on the Filter button. */
	activeFilterCount: number;
	isFilterPanelOpen: boolean;
	onToggleFilterPanel: () => void;
	canSyncFromLinkedin: boolean;
	isSyncing: boolean;
	onSync: () => void;
	onUpload: () => void;
	onSelectColumns: () => void;
}

export const LinkedinConnectionsToolbar: FunctionComponent<LinkedinConnectionsToolbarProps> = ({
	searchValue,
	onSearchChange,
	activeFilterCount,
	isFilterPanelOpen,
	onToggleFilterPanel,
	canSyncFromLinkedin,
	isSyncing,
	onSync,
	onUpload,
	onSelectColumns,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);

	return (
		<div className="flex h-11 items-center gap-2 border-b border-border-light bg-white px-2">
			<div className="w-[240px] flex-shrink-0">
				<InputField
					className="!rounded-[10px]"
					classNameWrapper="!mb-0"
					handleChange={(e: ChangeEvent<HTMLInputElement>) => {
						onSearchChange(e.target.value);
					}}
					iconInside={<SvgIcon className="text-gray-500" svgIcon={MagnifyingGlass} />}
					name="connections-search"
					placeholder={ts("search")}
					size={ButtonSize.XS}
					value={searchValue}
				/>
			</div>

			<Button
				color={
					activeFilterCount > 0 || isFilterPanelOpen
						? ButtonColor.ACTION_SOFT
						: ButtonColor.ACTION_SECONDARY
				}
				image={<Icon className="mr-2" icon={faFilter} />}
				size={ButtonSize.S}
				testId="toggle-connection-filters"
				title={
					activeFilterCount > 0
						? `${ts("myConnections.filter")} (${activeFilterCount})`
						: ts("myConnections.filter")
				}
				onClick={onToggleFilterPanel}
			/>

			<div className="ml-auto flex items-center gap-2">
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2" icon={faColumns3} />}
					size={ButtonSize.S}
					title={ts("myConnections.columns")}
					onClick={onSelectColumns}
				/>
				{canSyncFromLinkedin && (
					<span data-onboarding="connection-sync-button">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<SvgIcon className="mr-2 h-[14px] w-[14px]" svgIcon={LinkedinSvg} />}
							isLoading={isSyncing}
							size={ButtonSize.S}
							testId="sync-linkedin-button"
							title={ts("syncFromLinkedin")}
							onClick={onSync}
						/>
					</span>
				)}
				<Button
					color={ButtonColor.ACTION}
					image={<SvgIcon className="mr-2 h-[14px] w-[14px]" svgIcon={Plus} />}
					size={ButtonSize.S}
					testId="upload-connections-button"
					title={t("basics.upload")}
					onClick={onUpload}
				/>
			</div>
		</div>
	);
};
